import { List } from 'immutable';

import * as PlaylistConstants from './constants';
import * as D from './deps';

/**
 * Return the time since last played (in seconds) of the last played track for the given playlist
 *
 * @param  {object} state - the redux state
 * @param  {string} playlistId - the id of the playlist to query
 * @returns {number} number (in seconds) since track last played
 */
export function getPlaylistTrackLastPlayed(state, playlistId) {
  return (
    state.playlist.getIn(
      [
        PlaylistConstants.PlaylistKeys.PLAYLISTS,
        playlistId,
        PlaylistConstants.PlaylistKeys.LAST_PLAYED,
      ],
      1000,
    ) / 1000
  ); // convert millseconds to seconds
}

export function getPlaylistTrackById(state, trackToken) {
  const tracks = state.entities.get('playlistTracks');
  const track = tracks.get(trackToken);
  return track ? track : null;
}

/**
 * Return the index of the current track for the provided playlist
 *
 * @param  {object} state - the redux state
 * @param  {string} playlistId - the id of the playlist to query
 * @returns {number} index of the current track (-1 if not found)
 */
export function getPlaylistCurrentIndex(state, playlistId) {
  return state.playlist.getIn(
    [
      PlaylistConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      PlaylistConstants.PlaylistKeys.CURRENT_INDEX,
    ],
    -1,
  );
}

export function getTrackType(track) {
  // tracks served from getFragment have a 'trackType' key, but those served from APS have a nested 'sessionItem' with a 'type'
  return track ? track.get('trackType', track.getIn(['sessionItem', 'type'], null)) : null;
}

/**
 * Return array of track id's for playlist
 *
 * @param  {object} state - the redux state
 * @param  {string} stationId - the id of the playlist/station
 * @returns {Array} array of track tokens
 */
export function getPlaylistTrackIds(state, stationId) {
  return state.playlist.getIn(
    [PlaylistConstants.PlaylistKeys.PLAYLISTS, stationId, PlaylistConstants.PlaylistKeys.TRACKS],
    List(),
  );
}

export function getPrefetchedPlaylistTrackIds(state, stationId) {
  return state.playlist.getIn(
    [
      PlaylistConstants.PlaylistKeys.PLAYLISTS,
      stationId,
      PlaylistConstants.PlaylistKeys.PREFETCHED_TRACKS,
    ],
    List(),
  );
}

export function hasPrefetchedTracks(state, stationId) {
  const prefetchedTracks = state.playlist.getIn(
    [
      PlaylistConstants.PlaylistKeys.PLAYLISTS,
      stationId,
      PlaylistConstants.PlaylistKeys.PREFETCHED_TRACKS,
    ],
    List(),
  );
  return prefetchedTracks && prefetchedTracks.size > 0;
}

/**
 *
 * @param {object} state
 * @returns {*}
 */
export function getCurrentPlaylistType(state) {
  return getPlaylistType(state, getCurrentPlaylist(state));
}

/*
 * This util is for grabbing a track that has audio data like audioUrl and audioSkipUrl.
 * Tracks that come back from getFragment have all the data in one place, but in the on demand
 * world we get our audio from one api and the metadata from another. So this util
 * exists so in contexts when we need the audio data we can always make sure to get
 * the type of track that contains those audio properties if it exists.
 */
export function getAudioMapFromPlaylistIndex(state, playlistId, index) {
  const playlistTracks = getPlaylistTrackIds(state, playlistId);
  if (!playlistTracks.size) {
    return null;
  }
  const trackToken = playlistTracks.get(index);
  const track = D.PlaybackUtils.resolveAudioTrackForPlaylist(state, trackToken, playlistId);

  if (!track) {
    return null;
  }
  return track;
}

// Returns the last played track on the playlist if there is one
export function getLastPlayedTrackTokenOnPlaylist(state, playlistId) {
  if (!playlistId) return null;
  const currentIndex = getPlaylistCurrentIndex(state, playlistId);
  const playlistTracks = getPlaylistTrackIds(state, playlistId);
  if (!playlistTracks.size) return null;
  const lastAvailableIndex = Math.min(playlistTracks.size - 1, currentIndex);
  return playlistTracks.get(lastAvailableIndex);
}

export function isPlaylistOutOfTracks(state, id) {
  if (!id) return true;
  const currentIdx = getPlaylistCurrentIndex(state, id);
  const numTracks = getPlaylistTrackIds(state, id).size;
  return currentIdx >= numTracks;
}

export function isLastPlaylistIndex(state, id) {
  if (!id) return true;
  const currentIdx = getPlaylistCurrentIndex(state, id);
  const numTracks = getPlaylistTrackIds(state, id).size;
  return currentIdx === numTracks - 1;
}

export function areVoiceTracksEnabled(state, playlistId) {
  return state.playlist.getIn(
    [
      PlaylistConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      PlaylistConstants.PlaylistKeys.ARE_VOICE_TRACKS_ENABLED,
    ],
    true,
  );
}

export function canPlayVoiceTrack(state, voiceTrack, inPlaylistId) {
  if (!areVoiceTracksEnabled(state, inPlaylistId)) {
    return false;
  }

  return D.RightsUtils.hasInteractiveRights(voiceTrack); // TODO check w/ artists team why we need to check rights on this?
}

/**
 * The intent of this util is to signal if we've just been playing this playlist continually without pauses
 *
 * @param {object} state
 * @param {number} recencySec
 * @returns {boolean|*}
 */
export function hasCurrentPlaylistPlayedRecently(
  state,
  recencySec = D.PlaybackConstants.RECENTLY_PLAYED_TIME_WINDOW_SEC,
) {
  const playlistId = getCurrentPlaylist(state);
  const isPlaylistNotReset = getPlaylistCurrentIndex(state, playlistId) > -1;
  const hasPlayedRecently =
    Date.now() / 1000 - getPlaylistTrackLastPlayed(state, playlistId) <= recencySec;
  return isPlaylistNotReset && hasPlayedRecently;
}

/**
 * Return the current playlist
 *
 * @param  {object} state - the redux state
 * @returns {string} playlistId
 */
export function getCurrentPlaylist(state) {
  return state.playlist.getIn([PlaylistConstants.PlaylistKeys.CURRENT_PLAYLIST]);
}

export function getCurrentAudioMapFromPlaylist(state, playlistId) {
  const index = getPlaylistCurrentIndex(state, playlistId);
  return getAudioMapFromPlaylistIndex(state, playlistId, index);
}

export function getNextAudioMapFromPlaylist(state, playlistId) {
  const _isLastPlaylistIndex = isLastPlaylistIndex(state, playlistId);
  if (!_isLastPlaylistIndex) {
    const index = getPlaylistCurrentIndex(state, playlistId) + 1;
    const track = getAudioMapFromPlaylistIndex(state, playlistId, index);
    if (track) return track;
  } else {
    const prefetchedPlaylistTracks = getPrefetchedPlaylistTrackIds(state, playlistId);
    if (!prefetchedPlaylistTracks.size) {
      return null;
    }
    const trackToken = prefetchedPlaylistTracks.get(0);
    const track = D.PlaybackUtils.resolveAudioTrackForPlaylist(state, trackToken, playlistId);

    if (track) return track;
  }
  return null;
}

export function getCurrentAudioTrackFromActivePlaylist(state) {
  return getCurrentAudioMapFromPlaylist(state, getCurrentPlaylist(state));
}

/**
 *
 * @param {object} state
 * @param {string} playlistId
 * @returns {*}
 */
export function getPlaylistType(state, playlistId) {
  return state.playlist.getIn(
    [
      PlaylistConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      PlaylistConstants.PlaylistKeys.PLAYLIST_SOURCE_TYPE,
    ],
    null,
  );
}

/**
 * Return the time in seconds of the tracks current progress for the given playlist
 *
 * @param  {object} state - the redux state
 * @param  {string} playlistId - the id of the playlist to query
 * @returns {number} number (in seconds) of the playlist's current track progress
 */
export function getPlaylistTrackTime(state, playlistId) {
  //  TODO consolidate timings
  return state.playlist.getIn([
    PlaylistConstants.PlaylistKeys.PLAYLISTS,
    playlistId,
    PlaylistConstants.PlaylistKeys.CURRENT_TRACK_TIME,
  ]);
}

export function getCurrentPlaylistTrackTime(state) {
  return getPlaylistTrackTime(state, getCurrentPlaylist(state));
}

export function getSeekToTimeInSeconds(state, id) {
  if (!id) return 0;
  const lastPlayedTimeInSeconds = getPlaylistTrackLastPlayed(state, id);
  const elapsedTimeInSeconds = Date.now() / 1000 - lastPlayedTimeInSeconds;
  const trackTimeInSeconds = getPlaylistTrackTime(state, id);
  return trackTimeInSeconds + elapsedTimeInSeconds;
}

/**
 * Determines if a playlist is in the middle of playing a track and not an audio ad.
 *
 * @param {object} state - the redux store state
 * @param {string} playlistId - the id of the playlist to query
 * @returns {boolean} is this playlist currently in the middle of a track?
 */
export function isResumingPlaylistTrack(state, playlistId) {
  const trackToken = D.getInDomain('playback.utils.getPlaylistCurrentToken')(state, playlistId);
  const playlistTrack = getCurrentAudioMapFromPlaylist(state, playlistId);
  const playlistTrackToken = D.Utils.Core.getFromMap(playlistTrack, 'trackToken');
  const currentTime = getPlaylistTrackTime(state, playlistId);

  return playlistTrack && trackToken === playlistTrackToken && currentTime > 0;
}

export function shouldPlayNextTrackForId(state, id) {
  const seekToInSeconds = getSeekToTimeInSeconds(state, id);
  const durationTimeInSeconds = state.playlist.getIn(
    [
      PlaylistConstants.PlaylistKeys.PLAYLISTS,
      id,
      PlaylistConstants.PlaylistKeys.CURRENT_TRACK_DURATION,
    ],
    1,
  );
  const newPlaylist = getPlaylistTrackIds(state, id).size === 0;
  const currentIndex = getPlaylistCurrentIndex(state, id);

  return newPlaylist || currentIndex < 0 || seekToInSeconds >= durationTimeInSeconds;
}
